import cn from 'classnames';
import { forwardRef, useEffect, useMemo, useState } from 'react';

import { ClientBadge } from 'components/ClientBadge';
import { resolveProfileImageSrc } from 'helpers/profileImage';

export const Avatar = forwardRef<
  HTMLDivElement,
  {
    children?: React.ReactNode;
    className?: string;
    image: string | null | undefined;
    isClient?: boolean;
    placeholderName?: string | null;
    profileImageUrl?: string | null;
    shape?: 'circle' | 'rounded';
    size: 16 | 20 | 24 | 28 | 32 | 36 | 40 | 48 | 70 | 80 | 88;
  }
>(
  (
    {
      children,
      className,
      image: name,
      isClient,
      placeholderName,
      profileImageUrl,
      shape = 'circle',
      size,
    },
    ref,
  ) => {
    const [imageError, setImageError] = useState(false);

    useEffect(() => {
      setImageError(false);
    }, [name, profileImageUrl, size]);

    const src = useMemo(() => {
      if (imageError) {
        return undefined;
      }

      return resolveProfileImageSrc({
        profileImageUrl,
        size,
        storageKey: name,
      });
    }, [imageError, name, profileImageUrl, size]);

    return (
      <div
        ref={ref}
        className={cn(
          'relative shrink-0',
          {
            'h-10 w-10 text-18/120%': size === 40,
            'h-12 w-12 text-18/120%': size === 48,
            'h-20 w-20 text-32/120%': size === 80,
            'h-4 w-4 text-9/120%': size === 16,
            'h-6 w-6 text-10/120%': size === 24,
            'h-7 w-7 text-12/120%': size === 28,
            'h-8 w-8 text-12/120%': size === 32,
            'h-9 w-9 text-12/120%': size === 36,
            'h-[70px] w-[70px] text-32/120%': size === 70,
            'h-[88px] w-[88px] text-32/120%': size === 88,
            'text-body-25 h-5 w-5': size === 20,
          },
          className,
        )}
      >
        <div
          className={cn(
            'relative h-full w-full overflow-hidden bg-avatar-green',
            {
              'border border-gray-100':
                shape === 'rounded' && (size === 36 || size === 70),
              'border-2 border-solid border-gray-100':
                shape === 'circle' && size > 32,
              'rounded-[12px]':
                shape === 'rounded' && !(size === 24 || size === 36),
              'rounded-[4px]': shape === 'rounded' && size === 16,
              'rounded-[6px]':
                shape === 'rounded' &&
                (size === 24 || size === 36 || size === 70),
              'rounded-full': shape === 'circle',
            },
          )}
        >
          <span className="absolute left-0 top-0 z-0 flex h-full w-full items-center justify-center font-medium text-gray-900">
            {placeholderName?.slice(0, 2).toUpperCase()}
          </span>
          {src && (
            <img
              alt={placeholderName ?? undefined}
              className="absolute left-0 top-0 h-full w-full object-cover"
              decoding="async"
              loading="lazy"
              onError={() => {
                setImageError(true);
              }}
              src={src}
            />
          )}
        </div>
        {children}
        {isClient && <ClientBadge containerSize={size} shape={shape} />}
      </div>
    );
  },
);
